import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChoosingBar } from '../process/NeedsOpens.tsx';
import { Button } from '../ui.tsx';
import { useWide } from '../useWide.ts';
import { pressWhileChoosing, type Choosing } from './choosing.ts';
import { FocusChain } from './FocusChain.tsx';
import { cardPhase, earlySteps, linesOf, phaseCardKey, stepName, type TileLine, type TilePhase, type TileStep } from './model.ts';
import { PhoneRows } from './PhoneRows.tsx';
import { TreeBoard } from './TreeBoard.tsx';

/**
 * Every tree drawn one way (design study 12; Jason, 2026-10-06: "the client view of the tree, and the per project
 * admin tree configuration to all use the same styles that our master has been iterating on"): the master on the
 * process tree page, a job on its Process page, the client's sheet on both homes and the Introduction's example. On a
 * wide screen the whole tree (TreeBoard), a step opened with its whole chain and its card in the middle (FocusChain);
 * on a phone the phases as blocks of rows (PhoneRows), a step opened as its card. One way in and out of a step for all
 * of them: a click or a tap opens it, another step of its chain clicked opens that one, and Esc, Back or a click on the
 * paper round the chain go back to the whole tree. Opening brings the card's top into view; going back brings the step
 * that was opened into view, where it is in the tree. A task done early opens its step (design study 31, slice 4).
 *
 * What a page may do on the tree it is given and passes on: lines drawn by hand, a step moved to another phase, kinds
 * added from the tools (the board's), what a step carries at its foot setting a job up, a line of the chain clicked, and a
 * phase's heading opening the phase's card (`phaseCards`, the master's), opened and closed as a step's card is: its key
 * is `phaseCardKey`, and it opens as its card alone.
 *
 * Choosing what a step needs or opens (`choosing`, design study 31, slice 5; §5, A): the whole tree drawn in place of the
 * chain, the step being joined ringed and brought into view, each step it cannot join faint with why in words, a press
 * on any other picking it (one refused says why in the bar); over it the bar, "Choosing what Engineering needs: press a
 * step on the board, or type its name", its field and Cancel (Esc too). On a phone the bar and the field alone, the
 * board being rows. Nothing else is done on the board meanwhile; after a pick or Cancel the step's card opens again.
 */

/** Keys typed into a field are the field's: Esc there is not "back to the whole tree". */
const typing = (el: EventTarget | null) => el instanceof HTMLElement && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));

/** Brings an element of the drawing into view once it is drawn: the board and the chain lay themselves out a frame after they mount. */
function bringIntoView(root: HTMLElement | null, selector: string, block: ScrollLogicalPosition) {
  let tries = 0;
  let frame = 0;
  const look = () => {
    const el = root?.querySelector(selector);
    if (el) el.scrollIntoView({ block, behavior: 'auto' });
    else if (tries++ < 20) frame = requestAnimationFrame(look);
  };
  frame = requestAnimationFrame(look);
  return () => cancelAnimationFrame(frame);
}

export interface TreeViewProps {
  phases: readonly TilePhase[];
  steps: readonly TileStep[];
  /** The waits to draw; by default every step's needs. */
  lines?: readonly TileLine[];
  label: string;
  /**
   * The step opened, by key, or none; the page keeps it (and says what can be opened), TreeView opens and closes it. A
   * key the tree does not draw (a task in a step's list, on the master) opens as its card alone.
   */
  open?: string | null;
  /** The name of what is opened when the tree does not draw it. */
  titleOf?: (key: string) => string | null;
  onOpen?: (key: string | null) => void;
  /** A step opened, as its card; without one, nothing opens. */
  card?: (key: string) => ReactNode;
  /** The steps an opened chain is drawn from, when not all of them (a job leaves out what is only offered). */
  chainSteps?: readonly TileStep[];
  /** What "Back to …" goes back to: "the whole tree", "the whole job". */
  whole?: string;
  /** Over the whole tree, on a wide screen: the tools for adding to it. */
  tools?: ReactNode;
  /** Beside the bar of a step opened, on a wide screen (the save state). */
  aside?: ReactNode;
  /** Under the tools or the bar, over the drawing: what was done, what was refused, a question about a line. */
  between?: ReactNode;
  /** The board's: a step found, ringed and brought into view; every phase drawn even with nothing in it. */
  selected?: string | null;
  keepEmpty?: boolean;
  draw?: Parameters<typeof TreeBoard>[0]['draw'];
  onMoveToPhase?: (key: string, phase: string | null) => void;
  onAddKind?: (kind: string, phase: string | null) => void;
  arming?: { kind: string; word: string } | null;
  /** What a step carries at its foot setting a job up, on the board and on a phone. */
  actionsFor?: (key: string) => ReactNode;
  /** A phase's heading opens the phase's card (`phaseCardKey`), as a step's card opens: the master's, where its facts are set. */
  phaseCards?: boolean;
  /** The chain's: what can come after the step opened, and a line of it clicked. */
  after?: Parameters<typeof FocusChain>[0]['after'];
  onLine?: (line: TileLine) => void;
  /** Choosing a step for the opened one's needs or opens: the board drawn whole to press one, or its name typed. */
  choosing?: Choosing;
  /** How wide a step opened is drawn, when wider than every tree's 720 (the master's card, L-38). */
  cardWidth?: number;
}

export function TreeView(p: TreeViewProps) {
  const wide = useWide();
  const root = useRef<HTMLDivElement | null>(null);
  const { steps, onOpen } = p;
  const lines = useMemo(() => p.lines ?? linesOf(steps), [p.lines, steps]);
  const canOpen = Boolean(p.card && onOpen);
  // A task done early opens its step, where it is in the list (design study 31, slice 4): asked for, or clicked.
  const earlyStep = useMemo(() => earlySteps(steps), [steps]);
  const stepFor = (k: string) => earlyStep.get(k) ?? k;
  const open = canOpen && p.open ? stepFor(p.open) : null;
  const close = () => onOpen?.(null);
  const whole = p.whole ?? 'the whole tree';
  const choosing = p.choosing ?? null;
  const cancel = choosing?.onCancel;
  // The card is not shown while choosing: the board is, the step being joined in view on it.
  const shown = choosing ? null : open;
  // Why a step pressed on the board was not picked, for the choosing it was pressed in.
  const choosingAt = choosing ? `${choosing.side}:${choosing.key}` : null;
  const [said, setSaid] = useState<{ at: string; why: string } | null>(null);

  // Esc goes back to the whole tree, or out of choosing, unless it was typed into a field.
  useEffect(() => {
    if (!open && !cancel) return;
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || typing(e.target)) return;
      if (cancel) cancel();
      else onOpen?.(null);
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [open, onOpen, cancel]);

  // Opening brings the card's top into view; going back, or choosing, brings the step that was opened into view in the tree.
  const was = useRef<string | null>(null);
  useLayoutEffect(() => {
    const last = was.current;
    was.current = shown;
    if (shown === last) return;
    if (shown) return bringIntoView(root.current, '[data-card]', 'start');
    // A phase's card closed: back to its heading.
    const phase = cardPhase(last);
    if (phase !== null) return bringIntoView(root.current, `[data-phase="${CSS.escape(phase)}"]`, 'start');
    if (last) return bringIntoView(root.current, `[data-node="${CSS.escape(last)}"]`, 'center');
  }, [shown]);

  // A phase opened is named as its heading numbers it; a step by its name, in Title Case.
  const phaseAt = p.phases.findIndex((x) => x.key === cardPhase(open));
  const name = !open ? '' : phaseAt >= 0 ? `Phase ${String(phaseAt + 1).padStart(2, '0')} · ${p.phases[phaseAt]!.name}` : stepName(steps.find((s) => s.key === open)?.title ?? p.titleOf?.(open) ?? '');
  const opener = canOpen ? (k: string) => onOpen!(stepFor(k)) : undefined;
  const onPhase = canOpen && p.phaseCards ? (k: string) => onOpen!(phaseCardKey(k)) : undefined;

  // The bar while choosing: the step being joined by its name, and what its field finds (the board's steps, by default).
  const bar = choosing ? (
    <ChoosingBar
      key={choosingAt}
      name={stepName(steps.find((s) => s.key === choosing.key)?.title ?? p.titleOf?.(choosing.key) ?? '')}
      wide={wide}
      choosing={{ ...choosing, among: choosing.among ?? steps.filter((s) => !s.dot && !s.ghost).map((s) => ({ key: s.key, name: stepName(s.title) })) }}
      said={said && said.at === choosingAt ? said.why : null}
    />
  ) : null;
  const press = (k: string) => {
    if (!choosing || !choosingAt) return;
    const why = pressWhileChoosing(k, choosing);
    setSaid(why ? { at: choosingAt, why } : null);
  };

  if (!wide) {
    return (
      <div ref={root}>
        {p.between}
        {bar ?? (open ? (
          <div>
            <Button tone="quiet" onClick={close}>
              Back to {whole}
            </Button>
            <div data-card="" className="mt-2 scroll-mt-24 rounded-[2px] border-[1.5px] border-ink bg-sheet">
              {p.card!(open)}
            </div>
          </div>
        ) : (
          <PhoneRows phases={p.phases} steps={steps} label={p.label} onOpen={opener} actionsFor={p.actionsFor} onPhase={onPhase} />
        ))}
      </div>
    );
  }

  if (choosing) {
    return (
      <div ref={root}>
        {bar}
        {p.between}
        <TreeBoard phases={p.phases} steps={steps} lines={lines} label={p.label} selected={choosing.key} keepEmpty={p.keepEmpty} choose={{ key: choosing.key, refusal: choosing.refusal, onPress: press }} />
      </div>
    );
  }

  return (
    <div ref={root}>
      {open ? (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-y border-rule py-2.5">
          <span className="label !text-ink">Opened · {name}</span>
          <Button tone="quiet" onClick={close}>
            Back to {whole} (Esc)
          </Button>
          {p.aside ? <span className="ml-auto">{p.aside}</span> : null}
        </div>
      ) : (
        p.tools
      )}
      {p.between}
      {open ? (
        <FocusChain
          steps={p.chainSteps ?? steps}
          lines={lines}
          phases={p.phases}
          focus={open}
          card={p.card!(open)}
          label={phaseAt >= 0 ? name : `${name} and its chain`}
          after={p.after}
          onFocus={(k) => onOpen!(stepFor(k))}
          onClose={close}
          onLine={p.onLine}
          cardWidth={p.cardWidth}
        />
      ) : (
        <TreeBoard
          phases={p.phases}
          steps={steps}
          lines={lines}
          label={p.label}
          selected={p.selected}
          keepEmpty={p.keepEmpty}
          onOpen={opener}
          draw={p.draw}
          onMoveToPhase={p.onMoveToPhase}
          onAddKind={p.onAddKind}
          arming={p.arming}
          actionsFor={p.actionsFor}
          onPhase={onPhase}
        />
      )}
    </div>
  );
}
